/**
 * AdminActivities - Activities list view with filtering
 * Displays activities filtered by parent (account, lead, contact, order)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { crmAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { 
  Activity, 
  Phone, 
  Mail, 
  MessageSquare, 
  Calendar,
  Users,
  FileText,
  Loader2,
  Clock
} from 'lucide-react';

const ACTIVITY_TYPES = [
  { value: 'all', label: 'All Types', icon: Activity },
  { value: 'call', label: 'Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'meeting', label: 'Meeting', icon: Users },
  { value: 'note', label: 'Note', icon: FileText },
  { value: 'walk_in', label: 'Walk-in', icon: Calendar },
];

const getActivityIcon = (type) => {
  const found = ACTIVITY_TYPES.find(t => t.value === type);
  return found?.icon || Activity;
};

const getActivityColor = (type) => {
  switch (type) {
    case 'call': return 'bg-blue-100 text-blue-700';
    case 'email': return 'bg-purple-100 text-purple-700';
    case 'meeting': return 'bg-green-100 text-green-700';
    case 'note': return 'bg-gray-100 text-gray-700';
    case 'walk_in': return 'bg-yellow-100 text-yellow-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

/**
 * AdminActivities - Activities list with filtering support
 * @param {string} initialAccountId - Filter by account/customer ID
 * @param {string} initialLeadId - Filter by lead ID
 * @param {string} initialContactId - Filter by contact ID
 * @param {string} initialOrderId - Filter by order ID
 */
export default function AdminActivities({ 
  initialAccountId = null,
  initialLeadId = null,
  initialContactId = null,
  initialOrderId = null
}) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [total, setTotal] = useState(0);

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page_size: 100 };
      
      // Apply type filter
      if (typeFilter && typeFilter !== 'all') {
        params.type = typeFilter;
      }
      
      // Apply parent filters
      if (initialAccountId) {
        params.account_id = initialAccountId;
      }
      if (initialLeadId) {
        params.lead_id = initialLeadId;
      }
      if (initialContactId) {
        params.contact_id = initialContactId;
      }
      if (initialOrderId) {
        params.order_id = initialOrderId;
      }
      
      const data = await crmAPI.getActivities(params);
      setActivities(data.activities || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch activities:', err);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, initialAccountId, initialLeadId, initialContactId, initialOrderId]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffHours < 1) {
      return 'Just now';
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    }
    return d.toLocaleDateString();
  };

  // Get filter context description
  const getFilterContext = () => {
    if (initialAccountId) return 'Customer';
    if (initialLeadId) return 'Lead';
    if (initialContactId) return 'Contact';
    if (initialOrderId) return 'Order';
    return null;
  };

  const filterContext = getFilterContext();

  return (
    <div className="p-6" data-testid="admin-activities-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Activities</h1>
        <p className="text-gray-600">
          {filterContext 
            ? `Showing activities for this ${filterContext}` 
            : 'View all logged activities'}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex items-center gap-4">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-48" data-testid="activity-type-filter">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              {ACTIVITY_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  <div className="flex items-center gap-2">
                    <type.icon className="w-4 h-4" />
                    {type.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <span className="text-sm text-gray-500">
            {total} {total === 1 ? 'activity' : 'activities'}
          </span>
        </div>
      </div>

      {/* Activities List */}
      <div className="bg-white rounded-lg shadow-sm border">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No activities found</p>
            {filterContext && (
              <p className="text-sm text-gray-400 mt-1">
                No activities logged for this {filterContext.toLowerCase()}
              </p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {activities.map((activity) => {
              const Icon = getActivityIcon(activity.type);
              return (
                <div 
                  key={activity.id} 
                  className="p-4 hover:bg-gray-50 transition-colors"
                  data-testid={`activity-item-${activity.id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`p-2 rounded-lg ${getActivityColor(activity.type)}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs capitalize">
                          {activity.type?.replace('_', ' ')}
                        </Badge>
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(activity.occurred_at || activity.created_at)}
                        </span>
                      </div>
                      
                      {activity.subject && (
                        <p className="font-medium text-gray-900 mb-1">
                          {activity.subject}
                        </p>
                      )}
                      
                      {activity.body && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {activity.body}
                        </p>
                      )}
                      
                      {/* Linked entities */}
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        {activity.account_id && (
                          <span>Customer: {activity.account_id}</span>
                        )}
                        {activity.lead_id && (
                          <span>Lead: {activity.lead_id}</span>
                        )}
                        {activity.contact_id && (
                          <span>Contact: {activity.contact_id}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
